import {StrictMode, Suspense, lazy, useCallback} from 'react'
import {createRoot} from 'react-dom/client'
import {BrowserRouter, Routes, Route} from 'react-router-dom'
import {
  PlaygroundApp,
  HomePage,
  ConversationsListPage,
  ChatRoute,
  ShareArtifactPage,
  NotFoundPage,
  SkillsPage,
  SkillsNewPage,
  SkillDetailPage,
  AuthProvider,
  ToastProvider,
  useCoreSettingsSections,
  useCoreAccountSections,
  settingsRoutes,
  accountRoutes,
  configureApi,
  type AuthUser
} from '@livingdata/playground-core-front'
import '@livingdata/playground-core-front/styles.css'
import '@fontsource-variable/inter/index.css'
import '@fontsource-variable/inter/wght-italic.css'
import '@fontsource-variable/jetbrains-mono/index.css'
import '@fontsource-variable/jetbrains-mono/wght-italic.css'
import './brand.css'
import '@livingdata/playground-core-front/i18n'
import './i18n'

import {OAuthLoginPage} from './oauth-login-page'
import {brand} from './brand'
import {fetchSessionUser, signOut} from './api'

const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000'

configureApi({apiUrl, credentials: 'include'})

// Loaded on demand: the charter carries its own Markdown parser, and a page
// read once has no business in the bundle everyone downloads on arrival.
const CharterPage = lazy(async () => {
  const module = await import('./pages/charter')
  return {default: module.CharterPage}
})

// Loaded on demand, and never imported statically anywhere: the admin console
// has no business in the bundle every end user downloads. Its stylesheet
// travels separately because the built package's JS does not reference it.
const AdminConsole = lazy(async () => {
  const [module] = await Promise.all([
    import('@livingdata/playground-admin-front'),
    import('@livingdata/playground-admin-front/styles.css')
  ])
  return {default: module.AdminRoutes}
})

function App() {
  const settingsSections = useCoreSettingsSections()
  const accountSections = useCoreAccountSections()
  const logout = useCallback(async () => {
    // Nothing catches a failure here, so the page stays where it is: the
    // session lives on the server, and leaving for the login screen would tell
    // the user they are signed out while their cookie still works.
    await signOut()
    globalThis.location.href = '/'
  }, [])

  const loadUser = useCallback(async (): Promise<AuthUser | undefined> => {
    try {
      const user = await fetchSessionUser()

      // Rebuilt field by field: the session carries more than the three fields
      // the shared layout declares, and none of the rest has any business there.
      return user && {id: user.id, email: user.email, name: user.name}
    } catch {
      return undefined
    }
  }, [])

  return (
    <StrictMode>
      <AuthProvider fetchUser={loadUser}>
        <ToastProvider>
          <BrowserRouter>
            <Routes>
              <Route element={<PlaygroundApp loginPage={<OAuthLoginPage />} logout={logout} brand={brand} settingsSections={settingsSections} accountSections={accountSections} />}>
                <Route path='/' element={<HomePage />} />
                <Route path='/conversations/new' element={<ChatRoute />} />
                <Route path='/conversations' element={<ConversationsListPage />} />
                <Route path='/conversations/:conversationId' element={<ChatRoute />} />
                <Route
                  path='/charte'
                  element={
                    <Suspense fallback={null}>
                      <CharterPage />
                    </Suspense>
                  }
                />
                <Route path='/skills' element={<SkillsPage />} />
                <Route path='/skills/new' element={<SkillsNewPage />} />
                <Route path='/skills/:skillId' element={<SkillDetailPage />} />
                {settingsRoutes(settingsSections)}
                {accountRoutes(accountSections)}
              </Route>

              <Route
                path='/admin/*'
                element={
                  <Suspense fallback={null}>
                    <AdminConsole apiUrl={apiUrl} basePath='/admin' />
                  </Suspense>
                }
              />
              <Route path='/share/:shareToken' element={<ShareArtifactPage showWatermark={false} />} />
              {/* Legacy share URLs — token is read from the `shareToken` query param */}
              <Route path='/share/:conversationId/:artifactSlug' element={<ShareArtifactPage showWatermark={false} />} />
              <Route path='*' element={<NotFoundPage />} />
            </Routes>
          </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
    </StrictMode>
  )
}

createRoot(document.getElementById('root')!).render(<App />)
